import { useCallback, useEffect, useState } from 'react';
import { InstallHelpSheet } from '../../components/InstallHelpSheet';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { useInstallPrompt } from '../../lib/installPrompt';
import {
  currentSubscription,
  isPushSupported,
  readStoredDevice,
  subscriptionMatchesKey,
} from '../../lib/push';
import type { PushKind, PushSubscriptionRead } from '../../types/api';
import { useAuth } from '../auth/useAuth';
import {
  PUSH_KINDS,
  fetchPushConfig,
  forgetThisDevice,
  listPushSubscriptions,
  registerThisDevice,
  sendTestPush,
  updatePushSubscription,
} from './pushApi';

const KIND_LABELS: Record<PushKind, string> = {
  review_ready: 'Captions are ready to review',
  slides_ready: 'Slides or an image are ready to approve',
  run_failed: 'A post did not finish',
  plan_needs_review: 'A scheduled post needs your review',
  plan_failed: 'A scheduled post failed',
  published: 'A post went live on LinkedIn',
  publish_failed: 'A LinkedIn post failed',
};

/** What this card can say about this device. */
type View =
  | { kind: 'hidden' }
  | { kind: 'server_off' }
  | { kind: 'unsupported' }
  | { kind: 'ios_install' }
  | { kind: 'denied' }
  | { kind: 'off'; publicKey: string }
  | { kind: 'on'; publicKey: string; row: PushSubscriptionRead };

interface Loaded {
  userId: string;
  view: View;
}

function permission(): NotificationPermission {
  return 'Notification' in window ? Notification.permission : 'default';
}

/**
 * Works out whether this browser is subscribed, for this user, with the
 * server's current key. A subscription made with an older key is replaced
 * silently (the key was rotated, so the old one can never be delivered to).
 */
async function readView(userId: string, isIos: boolean, signal: AbortSignal): Promise<View> {
  let config;
  try {
    config = await fetchPushConfig(signal);
  } catch {
    // A backend without the route yet, or a failed read: say nothing.
    return { kind: 'hidden' };
  }
  if (!config.enabled || !config.public_key) return { kind: 'server_off' };
  const publicKey = config.public_key;

  if (!isPushSupported()) return isIos ? { kind: 'ios_install' } : { kind: 'unsupported' };
  if (permission() === 'denied') return { kind: 'denied' };

  const [subscription, rows] = await Promise.all([
    currentSubscription(),
    listPushSubscriptions(signal),
  ]);
  const stored = readStoredDevice(userId);
  const row = stored ? rows.find((entry) => entry.id === stored.id) : undefined;
  if (!subscription || !stored || !row || subscription.endpoint !== stored.endpoint) {
    return { kind: 'off', publicKey };
  }
  if (!subscriptionMatchesKey(subscription, publicKey) && permission() === 'granted') {
    const replaced = await registerThisDevice(userId, publicKey, row.kinds);
    return { kind: 'on', publicKey, row: replaced };
  }
  return { kind: 'on', publicKey, row };
}

function turnOnError(caught: unknown): string {
  if (caught instanceof DOMException && caught.name === 'NotAllowedError') {
    return 'Notifications were not allowed. Turn them on again and choose Allow.';
  }
  if (caught instanceof ApiError && caught.code === 'PUSH_ENDPOINT_INVALID') {
    return 'This browser uses a push service Autopostmate cannot send to.';
  }
  return getErrorMessage(caught, 'Could not turn on notifications. Try again.');
}

/**
 * Push notifications for this device (PUSH_NOTIFICATIONS_PLAN.md). Permission
 * is asked only when "Turn on notifications" is tapped, never on load. Each
 * browser is its own device with its own choice of what to hear about.
 */
export function NotificationsCard() {
  const { user } = useAuth();
  const install = useInstallPrompt();
  const userId = user?.id ?? null;
  const isIosBrowser = install.isIos && !install.isStandalone;

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();
    let cancelled = false;
    void (async () => {
      let view: View;
      try {
        view = await readView(userId, isIosBrowser, controller.signal);
      } catch {
        view = { kind: 'hidden' };
      }
      if (!cancelled) setLoaded({ userId, view });
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [userId, isIosBrowser]);

  const view = loaded !== null && loaded.userId === userId ? loaded.view : null;

  const show = useCallback(
    (next: View) => {
      if (userId) setLoaded({ userId, view: next });
    },
    [userId],
  );

  const turnOn = async (publicKey: string) => {
    if (!userId) return;
    setIsBusy(true);
    setError(null);
    setNotice(null);
    try {
      const row = await registerThisDevice(userId, publicKey);
      show({ kind: 'on', publicKey, row });
    } catch (caught) {
      if (permission() === 'denied') {
        show({ kind: 'denied' });
      } else if (caught instanceof ApiError && caught.code === 'INTEGRATION_DISABLED') {
        show({ kind: 'server_off' });
      } else {
        setError(turnOnError(caught));
      }
    } finally {
      setIsBusy(false);
    }
  };

  const turnOff = async (publicKey: string) => {
    if (!userId) return;
    setIsBusy(true);
    setError(null);
    setNotice(null);
    try {
      await forgetThisDevice(userId);
      show({ kind: 'off', publicKey });
    } catch (caught) {
      setError(getErrorMessage(caught, 'Could not turn off notifications. Try again.'));
    } finally {
      setIsBusy(false);
    }
  };

  const toggleKind = async (publicKey: string, row: PushSubscriptionRead, kind: PushKind) => {
    const kinds = row.kinds.includes(kind)
      ? row.kinds.filter((entry) => entry !== kind)
      : PUSH_KINDS.filter((entry) => entry === kind || row.kinds.includes(entry));
    setError(null);
    setNotice(null);
    // Shown at once; put back if the save fails.
    show({ kind: 'on', publicKey, row: { ...row, kinds } });
    try {
      const saved = await updatePushSubscription(row.id, { kinds });
      show({ kind: 'on', publicKey, row: saved });
    } catch (caught) {
      show({ kind: 'on', publicKey, row });
      setError(getErrorMessage(caught, 'Could not save that choice. Try again.'));
    }
  };

  const sendTest = async (row: PushSubscriptionRead) => {
    setIsBusy(true);
    setError(null);
    setNotice(null);
    try {
      await sendTestPush(row.id);
      setNotice('Test sent. It should arrive in a few seconds.');
    } catch (caught) {
      setError(getErrorMessage(caught, 'Could not send a test. Try again.'));
    } finally {
      setIsBusy(false);
    }
  };

  if (view === null || view.kind === 'hidden') return null;

  let lede: string;
  switch (view.kind) {
    case 'server_off':
      lede = 'Notifications are not set up on this server yet.';
      break;
    case 'unsupported':
      lede = 'This browser cannot show notifications from Autopostmate.';
      break;
    case 'ios_install':
      lede =
        'On an iPhone or iPad, notifications come through the installed app. Install Autopostmate to your home screen first, then turn them on there.';
      break;
    case 'denied':
      lede =
        'Notifications from Autopostmate are blocked in this browser. To allow them, open the site settings from the icon beside the address bar (on a phone, the app or site info), set Notifications to Allow, then reload this page.';
      break;
    case 'off':
      lede =
        'Hear on this device when captions or slides are ready, a post fails, or a scheduled post needs you. Nothing is sent while this is off.';
      break;
    case 'on':
      lede = `On for this device${view.row.label ? `, ${view.row.label}` : ''}. Choose what to hear about.`;
      break;
  }

  return (
    <section className="wizard settings-card">
      <div className="settings-card__head">
        <h2 className="wizard__title">Notifications</h2>
        <p className="settings-card__lede">{lede}</p>
      </div>

      {view.kind === 'ios_install' ? (
        <div>
          <button className="button" type="button" onClick={() => setIsHelpOpen(true)}>
            Show me how
          </button>
        </div>
      ) : null}

      {view.kind === 'off' ? (
        <div>
          <button
            className="button"
            type="button"
            disabled={isBusy}
            onClick={() => void turnOn(view.publicKey)}
          >
            {isBusy ? 'Turning on' : 'Turn on notifications'}
          </button>
        </div>
      ) : null}

      {view.kind === 'on' ? (
        <>
          <fieldset className="push-kinds">
            <legend className="push-kinds__legend">Tell me when</legend>
            {PUSH_KINDS.map((kind) => (
              <label key={kind} className="field field--inline push-kinds__option">
                <input
                  type="checkbox"
                  checked={view.row.kinds.includes(kind)}
                  onChange={() => void toggleKind(view.publicKey, view.row, kind)}
                />
                <span>{KIND_LABELS[kind]}</span>
              </label>
            ))}
          </fieldset>
          <div className="push-kinds__actions">
            <button
              className="button"
              type="button"
              disabled={isBusy}
              onClick={() => void sendTest(view.row)}
            >
              Send a test
            </button>
            <button
              className="button button--ghost"
              type="button"
              disabled={isBusy}
              onClick={() => void turnOff(view.publicKey)}
            >
              Turn off on this device
            </button>
          </div>
        </>
      ) : null}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="form__notice" role="status">
          {notice}
        </p>
      ) : null}
      {isHelpOpen ? <InstallHelpSheet onDismiss={() => setIsHelpOpen(false)} /> : null}
    </section>
  );
}
